import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import BuildIcon from '@mui/icons-material/Build';
import CheckIcon from '@mui/icons-material/Check';
import DateRangeIcon from '@mui/icons-material/DateRange';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import SailingIcon from '@mui/icons-material/Sailing';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Checkbox, Container, FormControlLabel, FormGroup, Stack, TextField, InputAdornment, IconButton } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import { useEffect, useState } from 'react';
import { CriteriaAction } from '../../features/criteria.feature';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { applyVesselTypeCascade, DateRanges, TextSlider, VesselTypeKey } from '../shared';
import { AbsColor } from '../../constants';

interface IList {
    VesselType: boolean;
    ConstructionDate: boolean;
    EntryIntoForceDate: boolean;
    MandatoryGuideline: boolean;
    HardwareOperational: boolean;
    RetroactiveNew: boolean;
    AdvanceFilters: boolean;
    VesselSize: boolean;
    ContractDate: boolean;
    KeelLayDate: boolean;
    DeliveryDate: boolean;
    Convention: boolean;
    ExpiryDate: boolean;
    UserSearchQuery: string;
}

const TrackerCriteriaFilter = (): JSX.Element => {
    const dispatch = useAppDispatch();

    const criteria = useAppSelector((state: any) => state.Criteria.TrackerCriteria);

    const [searchQuery, setSearchQuery] = useState<string>('');

    const ListState: IList = {
        VesselType: false,
        ConstructionDate: false,
        EntryIntoForceDate: false,
        MandatoryGuideline: false,
        HardwareOperational: false,
        RetroactiveNew: false,
        AdvanceFilters: false,
        VesselSize: false,
        ContractDate: false,
        KeelLayDate: false,
        DeliveryDate: false,
        Convention: false,
        ExpiryDate: false,
        UserSearchQuery: ''
    };

    const [myList, setMyList] = useState<IList>(ListState);

    const CriteriaFilterStyle = {
        IconStyle: {
            color: AbsColor.White1
        },
        MenuStyle: {
            backgroundColor: AbsColor.Blue1,
            color: AbsColor.White1
        }
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handleEnterKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            dispatch(CriteriaAction.SetCriteria({ ...criteria, UserSearchQuery: searchQuery.trim() }));
        }
        if (e.key === 'Delete') {
            handleClearSearch();
        }
    };

    const handleClearSearch = () => {
        setSearchQuery('');
        dispatch(CriteriaAction.SetCriteria({ ...criteria, UserSearchQuery: '' }));
    };

    /** Select/deselect a vessel type and cascade the same value to all succeeding levels. */
    const handleVesselTypeToggle = (key: VesselTypeKey) => {
        dispatch(CriteriaAction.SetCriteria(applyVesselTypeCascade(criteria, key)));
    };

    useEffect(() => {
        const updatedSearchQuery = criteria.UserSearchQuery || '';
        setMyList({ ...myList, UserSearchQuery: updatedSearchQuery });
        setSearchQuery(updatedSearchQuery);
    }, [criteria.UserSearchQuery]);

    return (
        <>
            <Container disableGutters maxWidth={false}>
                {/* Left Panel - Filter Panel */}
                <Stack direction={'column'}>
                    {/* Filter : Tracker Criteria Filter */}
                    <List
                        sx={{ width: '100%', bgcolor: 'background.paper' }}
                        component="nav"
                        aria-labelledby="Criteria Filter"
                        // subheader={
                        //     // <ListSubheader component="div" id="Criteria Filter">
                        //     //     Vessel Criteria Filters
                        //     // </ListSubheader>
                        // }
                    >
                        {/*User Regulation Search*/}
                        <TextField
                            variant="outlined"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onKeyDown={handleEnterKeyDown}
                            style={{ width: '100%' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleClearSearch}>
                                            <ClearIcon />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 0 // Removes the curve
                                }
                            }}
                        />
                        {/* Filter : Vessel Type */}
                        <ListItemButton onClick={() => setMyList({ ...myList, VesselType: !myList.VesselType })} style={{ ...CriteriaFilterStyle.MenuStyle }}>
                            <ListItemIcon>
                                <DirectionsBoatIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                            </ListItemIcon>
                            <ListItemText primary="Vessel Type" />
                            {myList.VesselType ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={myList.VesselType} timeout="auto" unmountOnExit sx={{ pl: 4 }}>
                            <FormGroup>
                                {/* Level 1: Passenger */}
                                <FormControlLabel
                                    label="Passenger"
                                    control={
                                        <Checkbox
                                            checked={criteria.PassengerShip}
                                            indeterminate={!criteria.PassengerShip && criteria.CruisePassenger}
                                            onChange={() => handleVesselTypeToggle('PassengerShip')}
                                        />
                                    }
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
                                    <FormControlLabel
                                        label="Cruise Passenger"
                                        control={
                                            <Checkbox
                                                checked={criteria.CruisePassenger}
                                                onChange={() => handleVesselTypeToggle('CruisePassenger')}
                                            />
                                        }
                                    />
                                </Box>

                                {/* Level 1: RoRo Vessel */}
                                <FormControlLabel
                                    label="RoRo Vessel"
                                    control={
                                        <Checkbox
                                            checked={criteria.RoRoVessel}
                                            indeterminate={!criteria.RoRoVessel && (criteria.RoRoVehicleCarrier || criteria.RoRoCargoShip || criteria.RoRoPassengerShip)}
                                            onChange={() => handleVesselTypeToggle('RoRoVessel')}
                                        />
                                    }
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
                                    <FormControlLabel
                                        label="RoRo Vehicle Carrier"
                                        control={
                                            <Checkbox
                                                checked={criteria.RoRoVehicleCarrier}
                                                onChange={() => handleVesselTypeToggle('RoRoVehicleCarrier')}
                                            />
                                        }
                                    />
                                    <FormControlLabel
                                        label="RoRo Cargo Ship"
                                        control={
                                            <Checkbox checked={criteria.RoRoCargoShip} onChange={() => handleVesselTypeToggle('RoRoCargoShip')} />
                                        }
                                    />
                                    <FormControlLabel
                                        label="RoRo Passenger Ship"
                                        control={
                                            <Checkbox
                                                checked={criteria.RoRoPassengerShip}
                                                onChange={() => handleVesselTypeToggle('RoRoPassengerShip')}
                                            />
                                        }
                                    />
                                </Box>

                                {/* Level 1: High-Speed Craft */}
                                <FormControlLabel
                                    checked={criteria.HighSpeedCraft}
                                    control={<Checkbox onChange={() => handleVesselTypeToggle('HighSpeedCraft')} />}
                                    label="High-Speed Craft"
                                />

                                {/* Level 1: Cargo Ship */}
                                <FormControlLabel
                                    label="Cargo Ship"
                                    control={
                                        <Checkbox
                                            checked={criteria.CargoShip}
                                            indeterminate={
                                                !criteria.CargoShip &&
                                                (criteria.ContainerCarrier ||
                                                    criteria.GeneralCargoCarrier ||
                                                    criteria.RefrigeratedCargoCarrier ||
                                                    criteria.Tanker ||
                                                    criteria.OilTanker ||
                                                    criteria.CrudeTanker ||
                                                    criteria.ProductTanker ||
                                                    criteria.ChemicalCarrier ||
                                                    criteria.GasLNGCarrier ||
                                                    criteria.LNGCarrier ||
                                                    criteria.NonLNGGasCarrier ||
                                                    criteria.BulkCarrier ||
                                                    criteria.CombinationCarrier ||
                                                    criteria.OreCarrier ||
                                                    criteria.OffshoreSupportVessel)
                                            }
                                            onChange={() => handleVesselTypeToggle('CargoShip')}
                                        />
                                    }
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
                                    <FormControlLabel
                                        label="Container Carrier"
                                        control={
                                            <Checkbox
                                                checked={criteria.ContainerCarrier}
                                                onChange={() => handleVesselTypeToggle('ContainerCarrier')}
                                            />
                                        }
                                    />
                                    <FormControlLabel
                                        label="General Cargo Carrier"
                                        control={
                                            <Checkbox
                                                checked={criteria.GeneralCargoCarrier}
                                                onChange={() => handleVesselTypeToggle('GeneralCargoCarrier')}
                                            />
                                        }
                                    />
                                    <FormControlLabel
                                        label="Refrigerated Cargo Carrier"
                                        control={
                                            <Checkbox
                                                checked={criteria.RefrigeratedCargoCarrier}
                                                onChange={() => handleVesselTypeToggle('RefrigeratedCargoCarrier')}
                                            />
                                        }
                                    />

                                    {/* Level 2: Tanker */}
                                    <FormControlLabel
                                        label="Tanker"
                                        control={
                                            <Checkbox
                                                checked={criteria.Tanker}
                                                indeterminate={
                                                    !criteria.Tanker &&
                                                    (criteria.OilTanker ||
                                                        criteria.CrudeTanker ||
                                                        criteria.ProductTanker ||
                                                        criteria.ChemicalCarrier ||
                                                        criteria.GasLNGCarrier ||
                                                        criteria.LNGCarrier ||
                                                        criteria.NonLNGGasCarrier)
                                                }
                                                onChange={() => handleVesselTypeToggle('Tanker')}
                                            />
                                        }
                                    />
                                    <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
                                        {/* Level 3: Oil Tanker */}
                                        <FormControlLabel
                                            label="Oil Tanker"
                                            control={
                                                <Checkbox
                                                    checked={criteria.OilTanker}
                                                    indeterminate={!criteria.OilTanker && (criteria.CrudeTanker || criteria.ProductTanker)}
                                                    onChange={() => handleVesselTypeToggle('OilTanker')}
                                                />
                                            }
                                        />
                                        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
                                            <FormControlLabel
                                                label="Crude Tanker"
                                                control={
                                                    <Checkbox
                                                        checked={criteria.CrudeTanker}
                                                        onChange={() => handleVesselTypeToggle('CrudeTanker')}
                                                    />
                                                }
                                            />
                                            <FormControlLabel
                                                label="Product Tanker"
                                                control={
                                                    <Checkbox
                                                        checked={criteria.ProductTanker}
                                                        onChange={() => handleVesselTypeToggle('ProductTanker')}
                                                    />
                                                }
                                            />
                                        </Box>

                                        <FormControlLabel
                                            label="Chemical Carrier"
                                            control={
                                                <Checkbox
                                                    checked={criteria.ChemicalCarrier}
                                                    onChange={() => handleVesselTypeToggle('ChemicalCarrier')}
                                                />
                                            }
                                        />

                                        {/* Level 3: Gas Carrier */}
                                        <FormControlLabel
                                            label="Gas Carrier"
                                            control={
                                                <Checkbox
                                                    checked={criteria.GasLNGCarrier}
                                                    indeterminate={!criteria.GasLNGCarrier && (criteria.LNGCarrier || criteria.NonLNGGasCarrier)}
                                                    onChange={() => handleVesselTypeToggle('GasLNGCarrier')}
                                                />
                                            }
                                        />
                                        <Box sx={{ display: 'flex', flexDirection: 'column', ml: 3 }}>
                                            <FormControlLabel
                                                label="LNG Carrier"
                                                control={
                                                    <Checkbox checked={criteria.LNGCarrier} onChange={() => handleVesselTypeToggle('LNGCarrier')} />
                                                }
                                            />
                                            <FormControlLabel
                                                label="Non-LNG Gas Carrier"
                                                control={
                                                    <Checkbox
                                                        checked={criteria.NonLNGGasCarrier}
                                                        onChange={() => handleVesselTypeToggle('NonLNGGasCarrier')}
                                                    />
                                                }
                                            />
                                        </Box>
                                    </Box>

                                    <FormControlLabel
                                        label="Bulk Carrier"
                                        control={<Checkbox checked={criteria.BulkCarrier} onChange={() => handleVesselTypeToggle('BulkCarrier')} />}
                                    />
                                    <FormControlLabel
                                        label="Combination Carrier"
                                        control={
                                            <Checkbox
                                                checked={criteria.CombinationCarrier}
                                                onChange={() => handleVesselTypeToggle('CombinationCarrier')}
                                            />
                                        }
                                    />
                                    <FormControlLabel
                                        label="Ore Carrier"
                                        control={<Checkbox checked={criteria.OreCarrier} onChange={() => handleVesselTypeToggle('OreCarrier')} />}
                                    />
                                    <FormControlLabel
                                        label="Offshore Support Vessel"
                                        control={
                                            <Checkbox
                                                checked={criteria.OffshoreSupportVessel}
                                                onChange={() => handleVesselTypeToggle('OffshoreSupportVessel')}
                                            />
                                        }
                                    />
                                </Box>

                                {/* Level 1: remaining top-level types */}
                                <FormControlLabel
                                    checked={criteria.FishingVessel}
                                    control={<Checkbox onChange={() => handleVesselTypeToggle('FishingVessel')} />}
                                    label="Fishing Vessel"
                                />
                                <FormControlLabel
                                    checked={criteria.DynamicallySupportedCraft}
                                    control={<Checkbox onChange={() => handleVesselTypeToggle('DynamicallySupportedCraft')} />}
                                    label="Dynamically Supported Craft"
                                />
                                <FormControlLabel
                                    checked={criteria.MobileOffshoreDrillingUnit}
                                    control={<Checkbox onChange={() => handleVesselTypeToggle('MobileOffshoreDrillingUnit')} />}
                                    label="Mobile Offshore Drilling Unit"
                                />
                                <FormControlLabel
                                    checked={criteria.SelfPropelled}
                                    control={<Checkbox onChange={() => handleVesselTypeToggle('SelfPropelled')} />}
                                    label="Self-Propelled Mobile Offshore Drilling Unit"
                                />
                            </FormGroup>
                        </Collapse>
                        {/* Construction Date */}
                        <ListItemButton onClick={() => setMyList({ ...myList, ConstructionDate: !myList.ConstructionDate })} style={{ ...CriteriaFilterStyle.MenuStyle }}>
                            <ListItemIcon>
                                <DensityMediumIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                            </ListItemIcon>
                            <ListItemText primary="Construction Date" />
                            {myList.ConstructionDate ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={myList.ConstructionDate} timeout="auto" unmountOnExit sx={{ pl: 4 }}>
                            {/* Contract Date */}
                            <ListItemButton onClick={() => setMyList({ ...myList, ContractDate: !myList.ContractDate })} sx={{ pl: 4 }}>
                                <ListItemIcon>
                                    <DateRangeIcon />
                                </ListItemIcon>
                                <ListItemText primary="Contract Date" />
                                {myList.ContractDate ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={myList.ContractDate} timeout="auto" unmountOnExit sx={{ pl: 7 }}>
                                <DateRanges
                                    onChangeBefore={(dateBefore: Date | null) => dispatch(CriteriaAction.SetCriteria({ ...criteria, ContractBeforeDate: dateBefore }))}
                                    onChangeAfter={undefined}
                                    defaultBefore={criteria.ContractBeforeDate}
                                    defaultAfter={null}
                                    tooltipMessage="If the exact date is unknown, specify the latest potential date"
                                    hideBeforeLabel={true}
                                />
                            </Collapse>
                            {/* Keel Lay Date */}
                            <ListItemButton onClick={() => setMyList({ ...myList, KeelLayDate: !myList.KeelLayDate })} sx={{ pl: 4 }}>
                                <ListItemIcon>
                                    <DateRangeIcon />
                                </ListItemIcon>
                                <ListItemText primary="Keel Lay Date" />
                                {myList.KeelLayDate ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={myList.KeelLayDate} timeout="auto" unmountOnExit sx={{ pl: 7 }}>
                                <DateRanges
                                    onChangeBefore={(dateBefore: Date | null) => dispatch(CriteriaAction.SetCriteria({ ...criteria, KeelLayBeforeDate: dateBefore }))}
                                    onChangeAfter={undefined}
                                    defaultBefore={criteria.KeelLayBeforeDate}
                                    defaultAfter={null}
                                    tooltipMessage="If the exact date is unknown, specify the latest potential date"
                                    hideBeforeLabel={true}
                                />
                            </Collapse>
                            {/* Delivery Date */}
                            <ListItemButton onClick={() => setMyList({ ...myList, DeliveryDate: !myList.DeliveryDate })} sx={{ pl: 4 }}>
                                <ListItemIcon>
                                    <DateRangeIcon />
                                </ListItemIcon>
                                <ListItemText primary="Delivery Date" />
                                {myList.DeliveryDate ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                            <Collapse in={myList.DeliveryDate} timeout="auto" unmountOnExit sx={{ pl: 7 }}>
                                <DateRanges
                                    onChangeBefore={(dateBefore: Date | null) => dispatch(CriteriaAction.SetCriteria({ ...criteria, DeliveryBeforeDate: dateBefore }))}
                                    onChangeAfter={undefined}
                                    defaultBefore={criteria.DeliveryBeforeDate}
                                    defaultAfter={null}
                                    tooltipMessage="If the exact date is unknown, specify the latest potential date"
                                    hideBeforeLabel={true}
                                />
                            </Collapse>
                        </Collapse>
                        {/* Entry into Effect Date */}
                        <ListItemButton onClick={() => setMyList({ ...myList, EntryIntoForceDate: !myList.EntryIntoForceDate })} style={{ ...CriteriaFilterStyle.MenuStyle }}>
                            <ListItemIcon>
                                <DateRangeIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                            </ListItemIcon>
                            <ListItemText primary="Entry into Effect Date" />
                            {myList.EntryIntoForceDate ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={myList.EntryIntoForceDate} timeout="auto" unmountOnExit sx={{ pl: 4 }}>
                            <DateRanges
                                onChangeBefore={undefined}
                                onChangeAfter={(dateAfter: Date | null) => dispatch(CriteriaAction.SetCriteria({ ...criteria, EntryIntoForceAfterDate: dateAfter }))}
                                defaultBefore={null}
                                defaultAfter={criteria.EntryIntoForceAfterDate}
                            />
                        </Collapse>
                        {/* Convention */}
                        <ListItemButton onClick={() => setMyList({ ...myList, Convention: !myList.Convention })} style={{ ...CriteriaFilterStyle.MenuStyle }}>
                            <ListItemIcon>
                                <AutoAwesomeMotionIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                            </ListItemIcon>
                            <ListItemText primary="Convention" />
                            {myList.Convention ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={myList.Convention} timeout="auto" unmountOnExit sx={{ pl: 7 }}>
                            <FormGroup>
                                <FormControlLabel
                                    checked={criteria.MARPOL}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, MARPOL: !criteria.MARPOL }))} />}
                                    label="MARPOL"
                                />
                                <FormControlLabel
                                    checked={criteria.SOLAS}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, SOLAS: !criteria.SOLAS }))} />}
                                    label="SOLAS"
                                />
                                <FormControlLabel
                                    checked={criteria.STCW}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, STCW: !criteria.STCW }))} />}
                                    label="STCW"
                                />
                                <FormControlLabel
                                    checked={criteria.AFS}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, AFS: !criteria.AFS }))} />}
                                    label="AFS"
                                />
                                <FormControlLabel
                                    checked={criteria.ICLL}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, ICLL: !criteria.ICLL }))} />}
                                    label="ICLL"
                                />
                                <FormControlLabel
                                    checked={criteria.BWM}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, BWM: !criteria.BWM }))} />}
                                    label="BWM"
                                />
                            </FormGroup>
                        </Collapse>
                        {/* Retroactive/New */}
                        <ListItemButton onClick={() => setMyList({ ...myList, RetroactiveNew: !myList.RetroactiveNew })} style={{ ...CriteriaFilterStyle.MenuStyle }}>
                            <ListItemIcon>
                                <FiberNewIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                            </ListItemIcon>
                            <ListItemText primary="Retroactive/New" />
                            {myList.RetroactiveNew ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={myList.RetroactiveNew} timeout="auto" unmountOnExit sx={{ pl: 4 }}>
                            <FormGroup>
                                <FormControlLabel
                                    checked={criteria.Retroactive}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, Retroactive: !criteria.Retroactive }))} />}
                                    label="Retroactive"
                                />
                                <FormControlLabel
                                    checked={criteria.New}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, New: !criteria.New }))} />}
                                    label="New"
                                />
                            </FormGroup>
                        </Collapse>
                        {/* Mandatory/Guideline */}
                        <ListItemButton onClick={() => setMyList({ ...myList, MandatoryGuideline: !myList.MandatoryGuideline })} style={{ ...CriteriaFilterStyle.MenuStyle }}>
                            <ListItemIcon>
                                <CheckIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                            </ListItemIcon>
                            <ListItemText primary="Mandatory/Guideline" />
                            {myList.MandatoryGuideline ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={myList.MandatoryGuideline} timeout="auto" unmountOnExit sx={{ pl: 4 }}>
                            <FormGroup>
                                <FormControlLabel
                                    checked={criteria.Mandatory}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, Mandatory: !criteria.Mandatory }))} />}
                                    label="Mandatory"
                                />
                                <FormControlLabel
                                    checked={criteria.Guideline}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, Guideline: !criteria.Guideline }))} />}
                                    label="Guideline"
                                />
                            </FormGroup>
                        </Collapse>
                        {/* Hardware/Operational */}
                        <ListItemButton onClick={() => setMyList({ ...myList, HardwareOperational: !myList.HardwareOperational })} style={{ ...CriteriaFilterStyle.MenuStyle }}>
                            <ListItemIcon>
                                <BuildIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                            </ListItemIcon>
                            <ListItemText primary="Hardware/Operational" />
                            {myList.HardwareOperational ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={myList.HardwareOperational} timeout="auto" unmountOnExit sx={{ pl: 4 }}>
                            <FormGroup>
                                <FormControlLabel
                                    checked={criteria.Hardware}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, Hardware: !criteria.Hardware }))} />}
                                    label="Hardware"
                                />
                                <FormControlLabel
                                    checked={criteria.Operational}
                                    control={<Checkbox onChange={() => dispatch(CriteriaAction.SetCriteria({ ...criteria, Operational: !criteria.Operational }))} />}
                                    label="Operational"
                                />
                            </FormGroup>
                        </Collapse>
                        {/* Advance Filters */}
                        {/* <ListItemButton onClick={() => setMyList({ ...myList, AdvanceFilters: !myList.AdvanceFilters })} style={{ ...CriteriaFilterStyle.MenuStyle }}>
                            <ListItemIcon>
                                <DensityMediumIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                            </ListItemIcon>
                            <ListItemText primary="Advance Filters" />
                            {myList.AdvanceFilters ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={myList.AdvanceFilters} timeout="auto" unmountOnExit> */}
                        {/* Vessel Size */}
                        <ListItemButton onClick={() => setMyList({ ...myList, VesselSize: !myList.VesselSize })} style={{ ...CriteriaFilterStyle.MenuStyle }}>
                            <ListItemIcon>
                                <SailingIcon style={{ ...CriteriaFilterStyle.IconStyle }} />
                            </ListItemIcon>
                            <ListItemText primary="Vessel Size" />
                            {myList.VesselSize ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                        <Collapse in={myList.VesselSize} timeout="auto" unmountOnExit sx={{ pl: 7 }}>
                            <TextSlider
                                key={'gross-tonnage'}
                                Label={'Gross Tonnage'}
                                onChange={(NewValue) => dispatch(CriteriaAction.SetCriteria({ ...criteria, GrossTonnage: NewValue }))}
                                DefaultValue={null}
                                ID={'gross-tonnage'}
                                Minimum={100}
                                Maximum={450000}
                                Step={100}
                            />
                            <TextSlider
                                key={'number-of-passengers'}
                                Label={'Number of Passengers'}
                                onChange={(NewValue) => dispatch(CriteriaAction.SetCriteria({ ...criteria, NumberOfPassengers: NewValue }))}
                                DefaultValue={null}
                                ID={'number-of-passengers'}
                                Minimum={1}
                                Maximum={8000}
                                Step={1}
                            />
                            <TextSlider
                                key={'deadweight'}
                                Label={'Deadweight (DWT)'}
                                onChange={(NewValue) => dispatch(CriteriaAction.SetCriteria({ ...criteria, Deadweight: NewValue }))}
                                DefaultValue={null}
                                ID={'deadweight'}
                                Minimum={100}
                                Maximum={600000}
                                Step={100}
                            />
                            <TextSlider
                                key={'load-line-length'}
                                Label={'Load Line Length (LLL)'}
                                onChange={(NewValue) => dispatch(CriteriaAction.SetCriteria({ ...criteria, LoadLineLength: NewValue }))}
                                DefaultValue={null}
                                ID={'load-line-length'}
                                Minimum={0}
                                Maximum={450}
                                Step={20}
                            />
                            <TextSlider
                                key={'length-overall'}
                                Label={'Length-Overall (LOA)'}
                                onChange={(NewValue) => dispatch(CriteriaAction.SetCriteria({ ...criteria, LengthOverall: NewValue }))}
                                DefaultValue={null}
                                ID={'length-overall'}
                                Minimum={0}
                                Maximum={500}
                                Step={24}
                            />
                        </Collapse>
                        {/* </Collapse> */}
                    </List>
                </Stack>
            </Container>
        </>
    );
};

export default TrackerCriteriaFilter;
